<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { Document, Download, View } from '@element-plus/icons-vue';
import SupportingDocumentService from '@/api/services/absenceSupportingDocument.service';
import Api from '@/api/Api';
import { useUiStore } from '@/app/stores/uiStore';
import notification from '@/utils/plugins/NotificationService';

// eslint-disable-next-line no-undef
const props = defineProps({ attachment: { type: [Object, String], required: true }, file: Boolean, disabled: Boolean, removable: { type: Boolean, default: true } });
// eslint-disable-next-line no-undef
const emit = defineEmits(['remove']);
const uiStore = useUiStore();
const busy = ref(false);
const previewUrl = ref('');
const previewKind = ref('');
const previewError = ref('');
const fileName = computed(() => typeof props.attachment === 'string'
  ? props.attachment.split('/').pop() : props.attachment.name || 'Justificante');
const pdfPreviewUrl = computed(() => previewUrl.value ? previewUrl.value + '#view=FitH' : '');
let generation = 0;

function revokePreview() {
  generation++;
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = '';
  previewKind.value = '';
  previewError.value = '';
}
watch(() => props.attachment, revokePreview);
onBeforeUnmount(revokePreview);

async function fetchFile() {
  if (props.file) return props.attachment;
  const attachment = props.attachment;
  const id = typeof attachment === 'string' ? attachment.split('/').pop()
    : attachment.id ?? attachment['@id']?.split('/').pop();
  if (!id) throw new Error('Justificante no disponible');
  const metadata = await SupportingDocumentService.getAbsenceAbsenceSupportingDocument(id);
  if (!metadata?.data?.contentUrl) throw new Error('Justificante no disponible');
  const response = await Api().get(process.env.VUE_APP_BASE_URI + metadata.data.contentUrl, { responseType: 'blob' });
  const type = response.headers?.['content-type'] || response.data?.type || '';
  return new Blob([response.data], { type });
}
function getPreviewType(blob) {
  const type = blob.type.toLowerCase().split(';')[0];
  if (type === 'application/pdf' || ((!type || type === 'application/octet-stream') && /\.pdf$/i.test(fileName.value))) return 'pdf';
  if (['image/jpeg', 'image/png'].includes(type) ||
      ((!type || type === 'application/octet-stream') && /\.(jpe?g|png)$/i.test(fileName.value))) return 'image';
  return '';
}
async function togglePreview() {
  if (busy.value || props.disabled) return;
  if (previewUrl.value) { revokePreview(); return; }
  const current = ++generation;
  previewError.value = '';
  busy.value = true;
  uiStore.startLoading();
  try {
    const blob = await fetchFile();
    if (current !== generation) return;
    const kind = getPreviewType(blob);
    if (!kind) {
      previewError.value = 'Vista previa no disponible para este tipo de archivo. Descárgalo para verlo.';
      return;
    }
    const type = kind === 'pdf' ? 'application/pdf'
      : ['image/jpeg', 'image/png'].includes(blob.type) ? blob.type
        : /\.png$/i.test(fileName.value) ? 'image/png' : 'image/jpeg';
    previewKind.value = kind;
    previewUrl.value = URL.createObjectURL(new Blob([blob], { type }));
  } catch {
    if (current === generation) previewError.value = 'No se ha podido cargar la vista previa. Vuelve a intentarlo.';
  } finally {
    busy.value = false;
    uiStore.stopLoading();
  }
}
async function downloadDocument() {
  if (busy.value || props.disabled) return;
  busy.value = true;
  uiStore.startLoading();
  let url;
  try {
    const blob = await fetchFile();
    url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.value;
    link.click();
  } catch {
    notification.addNotification({ type: 'error', message: 'Error al descargar el justificante.' });
  } finally {
    if (url) URL.revokeObjectURL(url);
    busy.value = false;
    uiStore.stopLoading();
  }
}
</script>

<template>
  <div class="absence-attachment">
    <div class="absence-attachment__row">
      <el-icon class="absence-attachment__icon"><Document /></el-icon>
      <div class="absence-attachment__name">
        <strong>{{ fileName }}</strong>
        <small v-if="file">Pendiente de guardar</small>
      </div>
      <div class="absence-attachment__actions">
        <button type="button" :disabled="disabled || busy" @click="togglePreview">
          <el-icon><View /></el-icon>{{ previewUrl ? 'Ocultar' : 'Ver' }}
        </button>
        <button type="button" :disabled="disabled || busy" @click="downloadDocument">
          <el-icon><Download /></el-icon>Descargar
        </button>
        <button v-if="removable" type="button" class="absence-attachment__remove" :disabled="disabled || busy"
          :aria-label="'Eliminar justificante ' + fileName" @click.stop="emit('remove')">
          <font-awesome-layers class="fa-lg">
            <font-awesome-icon icon="circle" />
            <font-awesome-icon icon="times" transform="shrink-6" class="text-white" />
          </font-awesome-layers>
        </button>
      </div>
    </div>
    <p v-if="previewError" role="alert" class="absence-attachment__error">{{ previewError }}</p>
    <div v-if="previewUrl" class="absence-attachment__preview">
      <iframe v-if="previewKind === 'pdf'" :src="pdfPreviewUrl" scrolling="no" :title="'Vista previa de ' + fileName" />
      <img v-else :src="previewUrl" :alt="'Vista previa de ' + fileName" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.absence-attachment {
  min-width: 0;
  &__row { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border: 1px solid #d4d9e5; border-radius: 16px; background: #fff; }
  &__icon { flex: 0 0 auto; color: #474ce6; background: #f1f2f8; border-radius: 8px; width: 40px; height: 48px; font-size: 26px; }
  &__name { flex: 1; min-width: 0; strong { display: block; font-size: 14px; overflow-wrap: anywhere; } small { display: block; color: #73767a; margin-top: 4px; } }
  &__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
  button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 0; background: transparent; color: #474ce6; font-size: 14px; font-weight: 600; min-height: 44px; margin: 0; padding: 6px 8px;
    .el-icon { font-size: 18px; } }
  button:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; border-radius: 8px; }
  button:disabled { cursor: not-allowed; opacity: .5; }
  &__remove { color: #f9156f !important; min-width: 44px; font-size: 15px; }
  &__error { color: #d31c5e; font-size: 13px; margin: 10px 0 0; }
  &__preview { width: 100%; min-width: 0; border: 1px solid #d4d9e5; border-radius: 8px; margin-top: 12px; overflow: hidden; }
  iframe { display: block; border: 0; width: 100%; max-width: 100%; height: 480px; }
  img { display: block; width: 100%; max-height: 480px; object-fit: contain; }
}
@media (max-width: 767px) {
  .absence-attachment {
    &__row { flex-wrap: wrap; align-items: flex-start; position: relative; padding-right: 62px; }
    &__actions { flex-wrap: wrap; width: 100%; }
    &__remove { position: absolute; top: 10px; right: 10px; }
  }
}
</style>
